import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

const VIEW_PARAM = 'view'

function readSegments(searchParams: URLSearchParams): string[] {
    const raw = searchParams.get(VIEW_PARAM) ?? ''
    return raw.split('/').filter(Boolean)
}

/**
 * URL-backed nested navigation. The `view` search param stores a slash-separated
 * path of segments; each component reads the segment at its own depth and
 * updating a parent automatically truncates all descendant segments — refreshing
 * the page restores the user to exactly the same nested view.
 *
 * Drop-in replacement for `useState<string | null>(null)` when the state
 * controls which nested screen is shown.
 */
export function useUrlPathSegment(index: number): [string | null, (next: string | null) => void] {
    const [searchParams, setSearchParams] = useSearchParams()
    const segments = useMemo(() => readSegments(searchParams), [searchParams])
    const value = segments[index] ?? null

    const setValue = useCallback(
        (next: string | null) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev)
                    const current = readSegments(params)
                    const updated = current.slice(0, index)
                    if (next !== null && next !== '') updated.push(next)
                    if (updated.length === 0) params.delete(VIEW_PARAM)
                    else params.set(VIEW_PARAM, updated.join('/'))
                    return params
                },
                { replace: true }
            )
        },
        [index, setSearchParams]
    )

    return [value, setValue]
}

/**
 * URL-backed numeric state stored under a dedicated search-param key. Used for
 * outer-tab selection so the user lands on the same tab after refresh.
 */
export function useUrlNumberParam(key: string, defaultValue: number): [number, (next: number) => void] {
    const [searchParams, setSearchParams] = useSearchParams()
    const raw = searchParams.get(key)
    const parsed = raw === null ? defaultValue : Number.parseInt(raw, 10)
    const value = Number.isFinite(parsed) ? parsed : defaultValue

    const setValue = useCallback(
        (next: number) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev)
                    if (next === defaultValue) params.delete(key)
                    else params.set(key, String(next))
                    return params
                },
                { replace: true }
            )
        },
        [key, defaultValue, setSearchParams]
    )

    return [value, setValue]
}
